/**
 * How well a destination matches what was typed into the Go box, as a rank
 * (lower is better). Tiers, best first: label prefix · a label word's prefix ·
 * a keyword's prefix · every typed word prefixing some label or keyword word ·
 * label substring · path substring · one typo against a label or keyword word.
 * Learned picks rank above all of these; `rankDestinations` applies them.
 */
import { DESTINATION_KEYWORDS } from './destinationKeywords';

import type { Destination } from './destinations';

/** Typo tolerance starts here: shorter text has too many one-edit neighbours. */
const MIN_TYPO_LENGTH = 4;

/** Lower-cased words of `text`, split on anything that is not a letter or digit. */
function wordsOf(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/**
 * Edit distance with adjacent transpositions (optimal string alignment), so
 * "jorunal" is one edit from "journal" as surely as "jornal" is.
 */
export function editDistance(a: string, b: string): number {
  const d: number[][] = Array.from({ length: a.length + 1 }, (_, i) =>
    Array.from({ length: b.length + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  );
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let best = Math.min(d[i - 1]![j]! + 1, d[i]![j - 1]! + 1, d[i - 1]![j - 1]! + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        best = Math.min(best, d[i - 2]![j - 2]! + 1);
      }
      d[i]![j] = best;
    }
  }
  return d[a.length]![b.length]!;
}

/** One edit from a whole word, or from the word's start while still being typed. */
function withinOneTypo(q: string, word: string): boolean {
  if (Math.abs(word.length - q.length) <= 1 && editDistance(q, word) <= 1) return true;
  return word.length > q.length && editDistance(q, word.slice(0, q.length)) <= 1;
}

/** What the tiers test, computed once per destination. */
interface MatchSubject {
  label: string;
  labelWords: string[];
  keywords: string[];
  /** Label words and keyword words together. */
  allWords: string[];
  path: string;
}

/** A tier's test; its position in `TIERS` is its rank. */
type Tier = (s: MatchSubject, q: string) => boolean;

const TIERS: readonly Tier[] = [
  (s, q) => s.label.startsWith(q),
  (s, q) => s.labelWords.some(w => w.startsWith(q)),
  (s, q) => s.keywords.some(k => k.startsWith(q) || wordsOf(k).some(w => w.startsWith(q))),
  (s, q) => {
    const tokens = wordsOf(q);
    return tokens.length > 1 && tokens.every(t => s.allWords.some(w => w.startsWith(t)));
  },
  (s, q) => s.label.includes(q),
  (s, q) => s.path.includes(q),
  (s, q) =>
    q.length >= MIN_TYPO_LENGTH && !/\s/.test(q) && s.allWords.some(w => withinOneTypo(q, w)),
];

/** The one-typo tier: a guess at what was meant, which never outranks an exact match. */
export const TYPO_RANK = TIERS.length - 1;

/** The rank of `d` for the normalised query `q`, or `undefined` when it does not match. */
export function matchRank(d: Destination, q: string): number | undefined {
  const label = d.label.toLowerCase();
  const keywords = (DESTINATION_KEYWORDS[d.id] ?? []).map(k => k.toLowerCase());
  const labelWords = wordsOf(label);
  const subject: MatchSubject = {
    label,
    labelWords,
    keywords,
    allWords: [...labelWords, ...keywords.flatMap(wordsOf)],
    path: d.path.toLowerCase(),
  };
  const rank = TIERS.findIndex(tier => tier(subject, q));
  return rank < 0 ? undefined : rank;
}
